import {
    DEFAULT_ROLE_ID,
    OWN_ROLE_ID,
    READ_ONLY_ROLE,
} from '@pages/project/settings/_utils/hooks/MembersAttributes/projectRoles';

/**
 * Single source of truth for project role permissions.
 *
 * Permissions are `scope:action` strings built from the closed lists below, and
 * roles are named bundles of them. To add a role or change what a role may do,
 * edit `ROLE_PERMISSIONS` only — the hook (`useProjectAccess`), `ActionGuard`,
 * `DisableGuard`, `useGuardedForm`, and the table/info-card enforcement all call
 * `can('scope:action')` and need no changes.
 */

/**
 * Permission scopes (the resource half).
 *   content → any project object (zone, asset, item, tra, survey, measure, mitigation, task)
 *   project → the project itself (edit / delete / copy)
 *   member  → project membership (manage)
 *
 * `content` is intentionally a single domain so all object mutations are covered by one rule.
 * If per-object granularity is ever needed (e.g. "may edit but not delete zones"), add a scope
 * here (e.g. 'zone') and check it at the relevant call site — no plumbing change.
 */
export const PERMISSION_SCOPES = ['content', 'project', 'member'] as const;
export type PermissionScope = (typeof PERMISSION_SCOPES)[number];

/** Permission actions (the verb half). */
export const PERMISSION_ACTIONS = [
    'create',
    'edit',
    'delete',
    'copy',
    'manage',
] as const;
export type PermissionAction = (typeof PERMISSION_ACTIONS)[number];

type Wildcard = '*';

/**
 * A permission a call site CHECKS via `can(...)`. The scope is always concrete;
 * the action may be a scope-wide wildcard (e.g. `content:*` = "any content action").
 */
export type Permission = `${PermissionScope}:${PermissionAction | Wildcard}`;

/**
 * A permission a ROLE is GRANTED. Either half may widen to `*`
 * (`content:*` = all content actions, `*:*` = everything).
 */
export type GrantedPermission =
    `${PermissionScope | Wildcard}:${PermissionAction | Wildcard}`;

const ROLE_PERMISSIONS: Record<string, GrantedPermission[]> = {
    [OWN_ROLE_ID]: ['*:*'],
    [DEFAULT_ROLE_ID]: ['content:*', 'project:edit', 'project:copy'],
    [READ_ONLY_ROLE]: ['project:copy'], // view is implicit; nothing mutating
};

function matches(granted: GrantedPermission, wanted: Permission): boolean {
    const [grantedResource, grantedAction] = granted.split(':');
    const [wantedResource, wantedAction] = wanted.split(':');
    return (
        (grantedResource === '*' || grantedResource === wantedResource) &&
        (grantedAction === '*' || grantedAction === wantedAction)
    );
}

/**
 * Pure resolver — used both inside a project and per-card on the projects list.
 * Project access relies solely on the project role; the global `admin` flag is
 * unrelated (it only governs the separate global admin pages).
 */
export function resolvePermissions(
    roleId: string | undefined,
): GrantedPermission[] {
    return roleId ? ROLE_PERMISSIONS[roleId] ?? [] : [];
}

export type CanFn = (wanted: Permission) => boolean;

export function makeCan(granted: GrantedPermission[]): CanFn {
    return (wanted: Permission) => granted.some((g) => matches(g, wanted));
}
